'use client';

import { useEffect } from 'react';

import { useRegionStore } from '@northladder/services';
import { useDeviceAssessmentValidationQuery } from '@northladder/apis';
import { Text } from '@northladder/i18n';
import {
  loadLocalData,
  saveLocalData,
  TTextLocalized,
} from '@northladder/utilities';

/**
 * This tracks persisted assessment device name in local storage.
 */
export const PERSISTED_ASSESSMENT_DEVICE_NAME = 'error-logs-ad.0.0.pname';

/**
 * This retrieves and decrypts the persisted assessment device name store from local storage.
 */
const loadPersistedDeviceName = () =>
  loadLocalData<TTextLocalized>(PERSISTED_ASSESSMENT_DEVICE_NAME);

/**
 * This Persists the assessment device name store to local storage.
 */
const persistAssessedDeviceName = (productName: TTextLocalized) => {
  saveLocalData(PERSISTED_ASSESSMENT_DEVICE_NAME, productName);
};

interface IDisplayDeviceNameProps {
  evaluationId: string;
}

/**
 * -----------------------------------------------------------------------------
 * This displays the current device name based on the `evaluationId` in session.
 */
export function DisplayDeviceName({ evaluationId }: IDisplayDeviceNameProps) {
  const baseURL = useRegionStore().tenantConfig?.apiBaseURL || '';
  const persistedDeviceName = loadPersistedDeviceName();

  const { isPending, error, data } = useDeviceAssessmentValidationQuery({
    evaluationId,
    isEnabled: !!baseURL,
  });

  const { productName } = data || {};

  useEffect(() => {
    if (productName) {
      persistAssessedDeviceName(productName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName]);

  if (isPending && !persistedDeviceName) {
    return <div className="rolling-loader" />;
  }

  if (error || !productName) return null;

  return (
    <p
      className="
        text-primary-600 dark:text-primary-400 max-w-md py-6 text-center text-base
        font-bold leading-4
      "
    >
      <Text text={productName} />
    </p>
  );
}
